import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Activity, Lock, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import DisclaimerFooter from "@/components/DisclaimerFooter";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    const res = await login(email.trim(), password);
    setBusy(false);
    if (!res.ok) {
      setErr(res.error || "Login failed");
    } else {
      const to = loc.state?.from?.pathname || "/";
      nav(to, { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-[#080B11] flex flex-col relative overflow-hidden">
      <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-cyan-500/40 to-transparent" />

      <div className="flex-1 flex items-center justify-center px-4 py-12 relative">
        <div className="w-full max-w-[420px]">
          {/* Brand */}
          <div className="flex items-center gap-2 mb-8">
            <span className="relative flex h-7 w-7 items-center justify-center border border-cyan-500/50 bg-cyan-500/10">
              <Activity className="h-4 w-4 text-cyan-400" />
            </span>
            <div className="flex flex-col leading-none">
              <span className="mono text-base font-bold tracking-tight text-slate-100">
                QUANT<span className="text-cyan-400">·</span>SIGNAL
              </span>
              <span className="mono text-[10px] uppercase tracking-widest text-slate-500 mt-1">
                Institutional Analytics · Phase 3 · Evidence-Based
              </span>
            </div>
          </div>

          {/* Card */}
          <div className="border border-[#1E293B] bg-[#0F1626] p-6 relative">
            <div className="absolute -top-px left-4 right-4 h-[1px] bg-gradient-to-r from-transparent via-cyan-500/60 to-transparent" />
            <div className="flex items-center gap-2 pb-4 border-b border-[#1E293B]">
              <Lock className="h-4 w-4 text-slate-400" />
              <span className="mono text-xs uppercase tracking-widest text-slate-400">
                Terminal Access
              </span>
            </div>

            <form onSubmit={submit} className="pt-5 space-y-4">
              <div>
                <label className="mono text-[10px] uppercase tracking-widest text-slate-500">
                  Email
                </label>
                <Input
                  data-testid="login-email-input"
                  type="text"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  className="mt-1 mono text-sm bg-[#0B111C] border-[#1E293B] focus-visible:border-cyan-500/60 focus-visible:ring-0 h-10"
                />
              </div>
              <div>
                <label className="mono text-[10px] uppercase tracking-widest text-slate-500">
                  Password
                </label>
                <Input
                  data-testid="login-password-input"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="mt-1 mono text-sm bg-[#0B111C] border-[#1E293B] focus-visible:border-cyan-500/60 focus-visible:ring-0 h-10"
                />
              </div>

              {err && (
                <div
                  data-testid="login-error-message"
                  className="mono text-[11px] text-red-300 border border-red-500/30 bg-red-500/10 px-3 py-2"
                >
                  {err}
                </div>
              )}

              <Button
                data-testid="login-submit-button"
                type="submit"
                disabled={busy}
                className="w-full h-10 bg-cyan-500 hover:bg-cyan-400 text-[#080B11] mono text-xs font-bold uppercase tracking-widest rounded-none"
              >
                {busy ? (
                  <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> Authenticating…</>
                ) : (
                  "Sign in →"
                )}
              </Button>
            </form>

            <div className="mt-5 pt-4 border-t border-[#1E293B] flex justify-between mono text-[10px] uppercase tracking-widest text-slate-600">
              <span>Single-user access</span>
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                System online
              </span>
            </div>
          </div>

          <p className="mono text-[10px] text-slate-600 text-center mt-5">
            Quantitative analysis platform. No orders are executed. No investment advice.
          </p>
        </div>
      </div>

      <DisclaimerFooter />
    </div>
  );
}
